import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api"; // Centralized authenticated Axios instance

export default function EcommCommissionManager() {
  const [salesmen, setSalesmen] = useState([]);
  const [channels, setChannels] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // New Channel / Marketplace Form State
  const [newChannel, setNewChannel] = useState({
    channelId: "",
    channelName: "",
    groupId: "",
  });

  // Track if we are editing an existing channel/marketplace row
  const [editingChannelId, setEditingChannelId] = useState(null);

  const initialFormState = {
    salesmanId: "",
    name: "",
    type: "ecommerce_manager",
    periodType: "monthly",
    flatRate: 5.0,
    isFlatAmount: false,
    assignedChannelIds: [],
  };
  const [managerForm, setManagerForm] = useState(initialFormState);

  const fetchData = useCallback(async () => {
    try {
      const managersRes = await api.get("/ecomm/managers");
      if (managersRes.data.success) setSalesmen(managersRes.data.data);

      const channelsRes = await api.get("/ecomm/channels");
      if (channelsRes.data.success) setChannels(channelsRes.data.data);
    } catch (error) {
      console.error("Error connecting to server:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setManagerForm(initialFormState);
  };

  const handleManagerSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editingId
      ? `/ecomm/managers/${editingId}`
      : "/ecomm/managers";

    try {
      if (editingId) {
        await api.put(endpoint, managerForm);
      } else {
        await api.post(endpoint, managerForm);
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error("Failed to save manager profile", error);
    }
  };

  // Handles both Adding AND Editing a channel/marketplace entity dynamically
  const handleChannelSubmit = async (e) => {
    e.preventDefault();
    if (!newChannel.channelId || !newChannel.channelName) return;

    const endpoint = editingChannelId
      ? `/ecomm/channels/${editingChannelId}`
      : "/ecomm/channels";

    try {
      if (editingChannelId) {
        await api.put(endpoint, newChannel);
      } else {
        await api.post(endpoint, newChannel);
      }
      setNewChannel({ channelId: "", channelName: "", groupId: "" });
      setEditingChannelId(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save channel entity", error);
    }
  };

  // Loads channel details directly into the sidebar edit form fields
  const startEditChannel = (channelId) => {
    const chan = channels.find(
      (c) => String(c.channelId) === String(channelId),
    );
    if (!chan) return;

    setEditingChannelId(chan.channelId);
    setNewChannel({
      channelId: chan.channelId,
      channelName: chan.channelName,
      groupId: chan.groupId || "",
    });
  };

  const cancelChannelEdit = () => {
    setEditingChannelId(null);
    setNewChannel({ channelId: "", channelName: "", groupId: "" });
  };

  const assignChannel = async (salesmanId, channelId) => {
    if (!channelId) return;
    const manager = salesmen.find((s) => s.salesmanId === salesmanId);
    if (!manager) return;

    const currentIds = [
      ...(manager.assignedChannelIds || []),
      String(channelId),
    ];
    sendAssignment(salesmanId, currentIds);
  };

  const removeChannel = async (salesmanId, channelId) => {
    const manager = salesmen.find((s) => s.salesmanId === salesmanId);
    if (!manager) return;

    const currentIds = (manager.assignedChannelIds || []).filter(
      (id) => id !== String(channelId),
    );
    sendAssignment(salesmanId, currentIds);
  };

  const sendAssignment = async (salesmanId, channelIds) => {
    try {
      await api.post("/ecomm/managers/assign-channels", {
        salesmanId,
        channelIds,
      });
      fetchData();
    } catch (error) {
      console.error("Failed to update channel assignment", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this manager?")) return;
    try {
      await api.delete(`/ecomm/managers/${id}`);
      fetchData();
    } catch (error) {
      console.error("Failed to delete manager", error);
    }
  };

  const unassignedChannels = channels.filter(
    (chan) =>
      !salesmen.some((s) =>
        s.assignedChannelIds?.includes(String(chan.channelId)),
      ),
  );

  const getChannelDetails = (id) => {
    const matched = channels.find((c) => String(c.channelId) === String(id));
    return matched
      ? { name: matched.channelName, groupId: matched.groupId || "ungrouped" }
      : { name: id, groupId: "ungrouped" };
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between mb-6 bg-white p-4 rounded-xl border border-slate-200">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              E-Commerce Commission Management
            </h1>
            <p className="text-xs text-slate-500">
              Route store channels to responsible managers and flat rates.
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs py-2 px-4 rounded-lg shadow transition"
          >
            + Add New Manager
          </button>
        </header>

        {/* Master Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Manager Cards */}
          <div className="lg:col-span-3 space-y-4">
            {salesmen.map((manager) => (
              <div
                key={manager.salesmanId}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
              >
                {/* Manager Header Info */}
                <div className="flex items-start justify-between border-b pb-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-800 text-base">
                        {manager.name}
                      </h3>
                      <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
                        ID: {manager.salesmanId}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 uppercase tracking-wide">
                      {manager.periodType} cycle
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-0.5 rounded-lg text-xs font-mono font-medium ${manager.isFlatAmount ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                    >
                      {manager.isFlatAmount
                        ? `$${manager.flatRate}`
                        : `${manager.flatRate}%`}
                    </span>
                    <button
                      onClick={() => {
                        setEditingId(manager.salesmanId);
                        setManagerForm(manager);
                        setIsModalOpen(true);
                      }}
                      className="text-xs text-indigo-600 hover:underline font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(manager.salesmanId)}
                      className="text-xs text-rose-600 hover:underline font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Manager Assigned Channels List */}
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1.5">
                    {manager.assignedChannelIds?.map((id) => {
                      const details = getChannelDetails(id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 bg-slate-100 border text-slate-700 text-xs px-2 py-1 rounded-md"
                        >
                          <span className="font-mono text-[10px] text-slate-400">
                            {id}
                          </span>
                          <span>{details.name}</span>

                          <span className="text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 rounded uppercase tracking-wider">
                            {details.groupId}
                          </span>

                          <button
                            type="button"
                            onClick={() => startEditChannel(id)}
                            className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium ml-1 bg-indigo-50 px-1 rounded border border-indigo-100"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() =>
                              removeChannel(manager.salesmanId, id)
                            }
                            className="text-slate-400 hover:text-slate-700 font-bold ml-0.5"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>

                  {/* Quick Assign Dropdown Selector */}
                  <div className="max-w-xs">
                    <select
                      onChange={(e) => {
                        assignChannel(manager.salesmanId, e.target.value);
                        e.target.value = "";
                      }}
                      className="w-full text-xs rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-600 focus:outline-indigo-600 font-medium"
                      defaultValue=""
                    >
                      <option value="" disabled>
                        + Assign store channel...
                      </option>
                      {unassignedChannels.map((c) => (
                        <option key={c.channelId} value={c.channelId}>
                          [{c.channelId}] {c.channelName} (
                          {c.groupId || "ungrouped"})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Column: Dynamic Channels Sidebar */}
          <div className="space-y-4">
            {/* Form Box (Handles New Channels and Channel Updates) */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-2.5">
                {editingChannelId
                  ? "Modify Marketplace"
                  : "Register New Channel"}
              </h3>
              <form onSubmit={handleChannelSubmit} className="space-y-2">
                <input
                  type="text"
                  placeholder="Channel Code (e.g., 770)"
                  className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 font-mono"
                  value={newChannel.channelId}
                  disabled={!!editingChannelId}
                  onChange={(e) =>
                    setNewChannel({ ...newChannel, channelId: e.target.value })
                  }
                  required
                />
                <input
                  type="text"
                  placeholder="Platform Name (e.g., TARGET)"
                  className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 uppercase"
                  value={newChannel.channelName}
                  onChange={(e) =>
                    setNewChannel({
                      ...newChannel,
                      channelName: e.target.value.toUpperCase(),
                    })
                  }
                  required
                />
                <input
                  type="text"
                  placeholder="Group ID (e.g., target, amazon, ebay)"
                  className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 lowercase"
                  value={newChannel.groupId}
                  onChange={(e) =>
                    setNewChannel({
                      ...newChannel,
                      groupId: e.target.value.toLowerCase(),
                    })
                  }
                />
                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    className="flex-1 text-white font-medium text-xs py-1.5 px-3 rounded-lg shadow transition bg-indigo-600 hover:bg-indigo-700"
                  >
                    {editingChannelId ? "Update Info" : "Add to Registry"}
                  </button>
                  {editingChannelId && (
                    <button
                      type="button"
                      onClick={cancelChannelEdit}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-600 border font-medium text-xs py-1.5 px-3 rounded-lg transition"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Unassigned Pool Visibility List with Edit Actions */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">
                Unassigned Channels Pool ({unassignedChannels.length})
              </h3>
              <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                {unassignedChannels.map((chan) => (
                  <div
                    key={chan.channelId}
                    className="p-2 rounded-lg bg-amber-50/60 border border-amber-100 text-slate-700 text-xs flex justify-between items-center gap-2"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-slate-800">
                          {chan.channelName}
                        </span>
                        <button
                          onClick={() => startEditChannel(chan.channelId)}
                          className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold shrink-0"
                        >
                          (Edit)
                        </button>
                      </div>
                      <span className="text-[9px] text-slate-400 mt-0.5 uppercase tracking-wider font-semibold">
                        Group: {chan.groupId || "ungrouped"}
                      </span>
                    </div>
                    <span className="font-mono text-[10px] bg-amber-100 px-1.5 py-0.5 rounded text-amber-800 shrink-0 font-bold">
                      {chan.channelId}
                    </span>
                  </div>
                ))}
                {unassignedChannels.length === 0 && (
                  <p className="text-xs text-slate-400 italic py-2 text-center">
                    All system channels assigned.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* POPUP MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-700">
                {editingId ? "Edit Manager" : "New Manager"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 font-bold text-lg"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleManagerSubmit} className="p-4 space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
                  Salesman ID
                </label>
                <input
                  type="number"
                  disabled={!!editingId}
                  className="w-full border rounded-lg px-2 py-1.5 text-xs bg-slate-50 disabled:bg-slate-100 focus:outline-indigo-600"
                  value={managerForm.salesmanId}
                  onChange={(e) =>
                    setManagerForm({
                      ...managerForm,
                      salesmanId: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
                  Name
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
                  value={managerForm.name}
                  onChange={(e) =>
                    setManagerForm({
                      ...managerForm,
                      name: e.target.value.toUpperCase(),
                    })
                  }
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
                    Cycle
                  </label>
                  <select
                    className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
                    value={managerForm.periodType}
                    onChange={(e) =>
                      setManagerForm({
                        ...managerForm,
                        periodType: e.target.value,
                      })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi_annual">Semi-Annual</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
                    Rate
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
                    value={managerForm.flatRate}
                    onChange={(e) =>
                      setManagerForm({
                        ...managerForm,
                        flatRate: parseFloat(e.target.value) || 0,
                      })
                    }
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
                  Rate Type
                </label>
                <select
                  className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
                  value={String(managerForm.isFlatAmount)}
                  onChange={(e) =>
                    setManagerForm({
                      ...managerForm,
                      isFlatAmount: e.target.value === "true",
                    })
                  }
                >
                  <option value="false">Percentage (%)</option>
                </select>
              </div>
              <div className="pt-3 border-t flex justify-end space-x-1.5 text-xs">
                <button
                  type="button"
                  onClick={closeModal}
                  className="border bg-white px-3 py-1.5 rounded-lg text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg shadow"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
// import React, { useState, useEffect } from "react";

// export default function EcommCommissionManager() {
//   const [salesmen, setSalesmen] = useState([]);
//   const [channels, setChannels] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [editingId, setEditingId] = useState(null);

//   // New Channel / Marketplace Form State
//   const [newChannel, setNewChannel] = useState({
//     channelId: "",
//     channelName: "",
//     groupId: "",
//   });

//   // Track if we are editing an existing channel/marketplace row
//   const [editingChannelId, setEditingChannelId] = useState(null);

//   const initialFormState = {
//     salesmanId: "",
//     name: "",
//     type: "ecommerce_manager",
//     periodType: "monthly",
//     flatRate: 5.0,
//     isFlatAmount: false,
//     assignedChannelIds: [],
//   };
//   const [managerForm, setManagerForm] = useState(initialFormState);

//   const fetchData = async () => {
//     try {
//       const managersRes = await fetch(
//         "http://10.5.21.42:5000/api/ecomm/managers",
//       );
//       const managersData = await managersRes.json();
//       if (managersData.success) setSalesmen(managersData.data);

//       const channelsRes = await fetch(
//         "http://10.5.21.42:5000/api/ecomm/channels",
//       );
//       const channelsData = await channelsRes.json();
//       if (channelsData.success) setChannels(channelsData.data);
//     } catch (error) {
//       console.error("Error connecting to server:", error);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   const closeModal = () => {
//     setIsModalOpen(false);
//     setEditingId(null);
//     setManagerForm(initialFormState);
//   };

//   const handleManagerSubmit = async (e) => {
//     e.preventDefault();
//     const url = editingId
//       ? `http://10.5.21.42:5000/api/ecomm/managers/${editingId}`
//       : "http://10.5.21.42:5000/api/ecomm/managers";
//     const method = editingId ? "PUT" : "POST";

//     try {
//       const res = await fetch(url, {
//         method,
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(managerForm),
//       });
//       if (res.ok) {
//         closeModal();
//         fetchData();
//       }
//     } catch (error) {
//       console.error("Failed to save manager profile", error);
//     }
//   };

//   // Handles both Adding AND Editing a channel/marketplace entity dynamically
//   const handleChannelSubmit = async (e) => {
//     e.preventDefault();
//     if (!newChannel.channelId || !newChannel.channelName) return;

//     const url = editingChannelId
//       ? `http://10.5.21.42:5000/api/ecomm/channels/${editingChannelId}`
//       : "http://10.5.21.42:5000/api/ecomm/channels";
//     const method = editingChannelId ? "PUT" : "POST";

//     try {
//       const res = await fetch(url, {
//         method,
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(newChannel),
//       });
//       if (res.ok) {
//         setNewChannel({ channelId: "", channelName: "", groupId: "" });
//         setEditingChannelId(null);
//         fetchData();
//       }
//     } catch (error) {
//       console.error("Failed to save channel entity", error);
//     }
//   };

//   // Loads channel details directly into the sidebar edit form fields
//   const startEditChannel = (channelId) => {
//     const chan = channels.find(
//       (c) => String(c.channelId) === String(channelId),
//     );
//     if (!chan) return;

//     setEditingChannelId(chan.channelId);
//     setNewChannel({
//       channelId: chan.channelId,
//       channelName: chan.channelName,
//       groupId: chan.groupId || "",
//     });
//   };

//   const cancelChannelEdit = () => {
//     setEditingChannelId(null);
//     setNewChannel({ channelId: "", channelName: "", groupId: "" });
//   };

//   const assignChannel = async (salesmanId, channelId) => {
//     if (!channelId) return;
//     const manager = salesmen.find((s) => s.salesmanId === salesmanId);
//     if (!manager) return;

//     const currentIds = [
//       ...(manager.assignedChannelIds || []),
//       String(channelId),
//     ];
//     sendAssignment(salesmanId, currentIds);
//   };

//   const removeChannel = async (salesmanId, channelId) => {
//     const manager = salesmen.find((s) => s.salesmanId === salesmanId);
//     if (!manager) return;

//     const currentIds = (manager.assignedChannelIds || []).filter(
//       (id) => id !== String(channelId),
//     );
//     sendAssignment(salesmanId, currentIds);
//   };

//   const sendAssignment = async (salesmanId, channelIds) => {
//     try {
//       const res = await fetch(
//         "http://10.5.21.42:5000/api/ecomm/managers/assign-channels",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({ salesmanId, channelIds }),
//         },
//       );
//       if (res.ok) fetchData();
//     } catch (error) {
//       console.error("Failed to update channel assignment", error);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Delete this manager?")) return;
//     await fetch(`http://10.5.21.42:5000/api/ecomm/managers/${id}`, {
//       method: "DELETE",
//     });
//     fetchData();
//   };

//   const unassignedChannels = channels.filter(
//     (chan) =>
//       !salesmen.some((s) =>
//         s.assignedChannelIds?.includes(String(chan.channelId)),
//       ),
//   );

//   const getChannelDetails = (id) => {
//     const matched = channels.find((c) => String(c.channelId) === String(id));
//     return matched
//       ? { name: matched.channelName, groupId: matched.groupId || "ungrouped" }
//       : { name: id, groupId: "ungrouped" };
//   };

//   return (
//     <div className="min-h-screen bg-slate-50 p-6 font-sans">
//       <div className="max-w-6xl mx-auto">
//         {/* Header */}
//         <header className="flex items-center justify-between mb-6 bg-white p-4 rounded-xl border border-slate-200">
//           <div>
//             <h1 className="text-xl font-bold text-slate-900">
//               E-Commerce Commission Management
//             </h1>
//             <p className="text-xs text-slate-500">
//               Route store channels to responsible managers and flat rates.
//             </p>
//           </div>
//           <button
//             onClick={() => setIsModalOpen(true)}
//             className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs py-2 px-4 rounded-lg shadow transition"
//           >
//             + Add New Manager
//           </button>
//         </header>

//         {/* Master Layout Grid */}
//         <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
//           {/* Left Column: Manager Cards */}
//           <div className="lg:col-span-3 space-y-4">
//             {salesmen.map((manager) => (
//               <div
//                 key={manager.salesmanId}
//                 className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
//               >
//                 {/* Manager Header Info */}
//                 <div className="flex items-start justify-between border-b pb-3 mb-4">
//                   <div>
//                     <div className="flex items-center gap-2">
//                       <h3 className="font-bold text-slate-800 text-base">
//                         {manager.name}
//                       </h3>
//                       <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
//                         ID: {manager.salesmanId}
//                       </span>
//                     </div>
//                     <p className="text-[11px] text-slate-400 font-mono mt-0.5 uppercase tracking-wide">
//                       {manager.periodType} cycle
//                     </p>
//                   </div>

//                   <div className="flex items-center gap-3">
//                     <span
//                       className={`px-2 py-0.5 rounded-lg text-xs font-mono font-medium ${manager.isFlatAmount ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
//                     >
//                       {manager.isFlatAmount
//                         ? `$${manager.flatRate}`
//                         : `${manager.flatRate}%`}
//                     </span>
//                     <button
//                       onClick={() => {
//                         setEditingId(manager.salesmanId);
//                         setManagerForm(manager);
//                         setIsModalOpen(true);
//                       }}
//                       className="text-xs text-indigo-600 hover:underline font-semibold"
//                     >
//                       Edit
//                     </button>
//                     <button
//                       onClick={() => handleDelete(manager.salesmanId)}
//                       className="text-xs text-rose-600 hover:underline font-semibold"
//                     >
//                       Delete
//                     </button>
//                   </div>
//                 </div>

//                 {/* Manager Assigned Channels List */}
//                 <div className="space-y-3">
//                   <div className="flex flex-wrap gap-1.5">
//                     {manager.assignedChannelIds?.map((id) => {
//                       const details = getChannelDetails(id);
//                       return (
//                         <span
//                           key={id}
//                           className="inline-flex items-center gap-1.5 bg-slate-100 border text-slate-700 text-xs px-2 py-1 rounded-md"
//                         >
//                           <span className="font-mono text-[10px] text-slate-400">
//                             {id}
//                           </span>
//                           <span>{details.name}</span>
//                           {/* <span className="text-[9px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 rounded uppercase tracking-wider">
//                             {details.groupId}
//                           </span> */}

//                           <span className="text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 rounded uppercase tracking-wider">
//                             {details.groupId}
//                           </span>

//                           {/* NEW: Inline Edit option for assigned channels */}
//                           <button
//                             type="button"
//                             onClick={() => startEditChannel(id)}
//                             className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium ml-1 bg-indigo-50 px-1 rounded border border-indigo-100"
//                           >
//                             Edit
//                           </button>

//                           <button
//                             onClick={() =>
//                               removeChannel(manager.salesmanId, id)
//                             }
//                             className="text-slate-400 hover:text-slate-700 font-bold ml-0.5"
//                           >
//                             ×
//                           </button>
//                         </span>
//                       );
//                     })}
//                   </div>

//                   {/* Quick Assign Dropdown Selector */}
//                   <div className="max-w-xs">
//                     <select
//                       onChange={(e) => {
//                         assignChannel(manager.salesmanId, e.target.value);
//                         e.target.value = "";
//                       }}
//                       className="w-full text-xs rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-600 focus:outline-indigo-600 font-medium"
//                       defaultValue=""
//                     >
//                       <option value="" disabled>
//                         + Assign store channel...
//                       </option>
//                       {unassignedChannels.map((c) => (
//                         <option key={c.channelId} value={c.channelId}>
//                           [{c.channelId}] {c.channelName} (
//                           {c.groupId || "ungrouped"})
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* Right Column: Dynamic Channels Sidebar */}
//           <div className="space-y-4">
//             {/* Form Box (Handles New Channels and Channel Updates) */}
//             <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
//               <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-2.5">
//                 {editingChannelId
//                   ? "Modify Marketplace"
//                   : "Register New Channel"}
//               </h3>
//               <form onSubmit={handleChannelSubmit} className="space-y-2">
//                 <input
//                   type="text"
//                   placeholder="Channel Code (e.g., 770)"
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 font-mono"
//                   value={newChannel.channelId}
//                   disabled={!!editingChannelId} // Channel Code locked during edits
//                   onChange={(e) =>
//                     setNewChannel({ ...newChannel, channelId: e.target.value })
//                   }
//                   required
//                 />
//                 <input
//                   type="text"
//                   placeholder="Platform Name (e.g., TARGET)"
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 uppercase"
//                   value={newChannel.channelName}
//                   onChange={(e) =>
//                     setNewChannel({
//                       ...newChannel,
//                       channelName: e.target.value.toUpperCase(),
//                     })
//                   }
//                   required
//                 />
//                 <input
//                   type="text"
//                   placeholder="Group ID (e.g., target, amazon, ebay)"
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600 lowercase"
//                   value={newChannel.groupId}
//                   onChange={(e) =>
//                     setNewChannel({
//                       ...newChannel,
//                       groupId: e.target.value.toLowerCase(),
//                     })
//                   }
//                 />
//                 <div className="flex gap-2 pt-1">
//                   <button
//                     type="submit"
//                     className="flex-1 text-white font-medium text-xs py-1.5 px-3 rounded-lg shadow transition bg-indigo-600 hover:bg-indigo-700"
//                   >
//                     {editingChannelId ? "Update Info" : "Add to Registry"}
//                   </button>
//                   {editingChannelId && (
//                     <button
//                       type="button"
//                       onClick={cancelChannelEdit}
//                       className="bg-slate-100 hover:bg-slate-200 text-slate-600 border font-medium text-xs py-1.5 px-3 rounded-lg transition"
//                     >
//                       Cancel
//                     </button>
//                   )}
//                 </div>
//               </form>
//             </div>

//             {/* Unassigned Pool Visibility List with Edit Actions */}
//             <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
//               <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">
//                 Unassigned Channels Pool ({unassignedChannels.length})
//               </h3>
//               <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
//                 {unassignedChannels.map((chan) => (
//                   <div
//                     key={chan.channelId}
//                     className="p-2 rounded-lg bg-amber-50/60 border border-amber-100 text-slate-700 text-xs flex justify-between items-center gap-2"
//                   >
//                     <div className="flex flex-col min-w-0 flex-1">
//                       <div className="flex items-center gap-2">
//                         <span className="truncate font-medium text-slate-800">
//                           {chan.channelName}
//                         </span>
//                         <button
//                           onClick={() => startEditChannel(chan.channelId)}
//                           className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold shrink-0"
//                         >
//                           (Edit)
//                         </button>
//                       </div>
//                       <span className="text-[9px] text-slate-400 mt-0.5 uppercase tracking-wider font-semibold">
//                         Group: {chan.groupId || "ungrouped"}
//                       </span>
//                     </div>
//                     <span className="font-mono text-[10px] bg-amber-100 px-1.5 py-0.5 rounded text-amber-800 shrink-0 font-bold">
//                       {chan.channelId}
//                     </span>
//                   </div>
//                 ))}
//                 {unassignedChannels.length === 0 && (
//                   <p className="text-xs text-slate-400 italic py-2 text-center">
//                     All system channels assigned.
//                   </p>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* POPUP MODAL */}
//       {isModalOpen && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
//           <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border overflow-hidden">
//             <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
//               <h2 className="text-sm font-bold text-slate-700">
//                 {editingId ? "Edit Manager" : "New Manager"}
//               </h2>
//               <button
//                 onClick={closeModal}
//                 className="text-slate-400 hover:text-slate-600 font-bold text-lg"
//               >
//                 ×
//               </button>
//             </div>
//             <form onSubmit={handleManagerSubmit} className="p-4 space-y-3">
//               <div>
//                 <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
//                   Salesman ID
//                 </label>
//                 <input
//                   type="number"
//                   disabled={!!editingId}
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs bg-slate-50 disabled:bg-slate-100 focus:outline-indigo-600"
//                   value={managerForm.salesmanId}
//                   onChange={(e) =>
//                     setManagerForm({
//                       ...managerForm,
//                       salesmanId: e.target.value,
//                     })
//                   }
//                   required
//                 />
//               </div>
//               <div>
//                 <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
//                   Name
//                 </label>
//                 <input
//                   type="text"
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
//                   value={managerForm.name}
//                   onChange={(e) =>
//                     setManagerForm({
//                       ...managerForm,
//                       name: e.target.value.toUpperCase(),
//                     })
//                   }
//                   required
//                 />
//               </div>
//               <div className="grid grid-cols-2 gap-3">
//                 <div>
//                   <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
//                     Cycle
//                   </label>
//                   <select
//                     className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
//                     value={managerForm.periodType}
//                     onChange={(e) =>
//                       setManagerForm({
//                         ...managerForm,
//                         periodType: e.target.value,
//                       })
//                     }
//                   >
//                     <option value="monthly">Monthly</option>
//                     <option value="quarterly">Quarterly</option>
//                     <option value="semi_annual">Semi-Annual</option>
//                     <option value="yearly">Yearly</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
//                     Rate
//                   </label>
//                   <input
//                     type="number"
//                     step="0.01"
//                     className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
//                     value={managerForm.flatRate}
//                     onChange={(e) =>
//                       setManagerForm({
//                         ...managerForm,
//                         flatRate: parseFloat(e.target.value) || 0,
//                       })
//                     }
//                     required
//                   />
//                 </div>
//               </div>
//               <div>
//                 <label className="block text-[11px] font-bold text-slate-500 uppercase mb-0.5">
//                   Rate Type
//                 </label>
//                 <select
//                   className="w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-indigo-600"
//                   value={String(managerForm.isFlatAmount)}
//                   onChange={(e) =>
//                     setManagerForm({
//                       ...managerForm,
//                       isFlatAmount: e.target.value === "true",
//                     })
//                   }
//                 >
//                   <option value="false">Percentage (%)</option>
//                 </select>
//               </div>
//               <div className="pt-3 border-t flex justify-end space-x-1.5 text-xs">
//                 <button
//                   type="button"
//                   onClick={closeModal}
//                   className="border bg-white px-3 py-1.5 rounded-lg text-slate-600"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg shadow"
//                 >
//                   Save
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
